// Helper para conversão de KML / KMZ / Shapefile (.zip) → GeoJSON FeatureCollection,
// e cálculos de área (m²), perímetro/comprimento (m) e bbox via @turf/turf.
// Tudo roda no client (sem Lambda extra).

// @ts-ignore — sem tipos oficiais (default export CJS)
import * as togeojsonModule from '@mapbox/togeojson';
// @ts-ignore — sem tipos oficiais
import shp from 'shpjs';
// @ts-ignore — JSZip CJS
import JSZip from 'jszip';
import * as turf from '@turf/turf';
import type { FeatureCollection } from 'geojson';

// Resolve se o módulo veio como `{ default: { kml, gpx } }` ou direto `{ kml, gpx }`
const togeojson: any = (togeojsonModule as any).default || togeojsonModule;

export interface GeoMetricas {
  area_m2: number;
  perimetro_m: number;
  comprimento_m: number;
  bbox: [number, number, number, number];  // [oeste, sul, leste, norte]
  n_features: number;
}

export interface GeoParseResult {
  geojson: FeatureCollection;
  metricas: GeoMetricas;
  tipo: 'kml' | 'kmz' | 'shp' | 'geojson';
}

/** Detecta tipo pelo nome do arquivo */
export function detectarTipoGeo(nome: string): 'kml' | 'kmz' | 'shp' | 'geojson' | null {
  const n = nome.toLowerCase();
  if (n.endsWith('.kml')) return 'kml';
  if (n.endsWith('.kmz')) return 'kmz';
  if (n.endsWith('.zip')) return 'shp';                     // shapefile vem zipado
  if (n.endsWith('.shp')) return 'shp';                     // shp solto não funciona — exigirá zip
  if (n.endsWith('.geojson') || n.endsWith('.json')) return 'geojson';
  return null;
}

/** Lê arquivo como ArrayBuffer */
function arquivoComoBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as ArrayBuffer);
    r.onerror = () => reject(new Error('Falha lendo arquivo'));
    r.readAsArrayBuffer(file);
  });
}

function arquivoComoTexto(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error('Falha lendo arquivo'));
    r.readAsText(file);
  });
}

/** KML em string → GeoJSON. Tenta XML estrito; se falhar, cai pra HTML (mais tolerante).
    KMLs do Google Earth e geradores brasileiros frequentemente têm XML mal-formado:
    & não escapado, encoding incorreto, BOM, namespaces não declarados — etc. */
function kmlParaGeojson(kmlText: string): FeatureCollection {
  if (!kmlText || !kmlText.trim()) throw new Error('KML vazio');
  if (typeof togeojson?.kml !== 'function') {
    throw new Error('togeojson.kml não disponível — falha de import');
  }
  // Remove BOM (Byte Order Mark) que alguns geradores deixam no início
  let texto = kmlText.replace(/^﻿/, '');
  // Strip de caracteres de controle inválidos em XML 1.0 (exceto tab/newline/cr)
  texto = texto.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');

  // Tentativa 1: parser XML estrito
  let dom: Document | null = null;
  let erroXml: string | null = null;
  try {
    const tentativa = new DOMParser().parseFromString(texto, 'application/xml');
    const erro = tentativa.querySelector('parsererror');
    if (!erro) {
      dom = tentativa;
    } else {
      // Extrai mensagem útil do <parsererror> pra contexto
      erroXml = (erro.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 200);
    }
  } catch (e: any) {
    erroXml = e.message;
  }

  // Tentativa 2 (fallback): parser HTML — mais tolerante a XML mal-formado.
  // togeojson aceita HTMLDocument com tags KML.
  if (!dom) {
    try {
      dom = new DOMParser().parseFromString(texto, 'text/html');
    } catch (e: any) {
      throw new Error(`KML mal-formado e fallback HTML também falhou: ${e.message}`);
    }
    if (!dom) throw new Error('Não foi possível parsear o KML em nenhum modo');
  }

  let fc: any;
  try {
    fc = togeojson.kml(dom);
  } catch (e: any) {
    throw new Error(
      `Falha convertendo KML para GeoJSON: ${e.message}` +
      (erroXml ? ` (erro XML original: ${erroXml})` : '')
    );
  }
  if (!fc || !Array.isArray(fc.features)) {
    throw new Error(
      `KML não produziu FeatureCollection válida` +
      (erroXml ? `. Erro XML: ${erroXml}` : '. O arquivo pode não conter Placemarks visíveis.')
    );
  }
  return fc as FeatureCollection;
}

/** KMZ (ZIP com KML dentro) → GeoJSON. */
async function kmzParaGeojson(buf: ArrayBuffer): Promise<FeatureCollection> {
  if (typeof JSZip?.loadAsync !== 'function') {
    throw new Error('JSZip não disponível — falha de import');
  }
  let zip: any;
  try {
    zip = await JSZip.loadAsync(buf);
  } catch (e: any) {
    throw new Error('KMZ corrompido ou não é um arquivo ZIP válido: ' + e.message);
  }
  // Acha o primeiro .kml dentro (ignora pastas, ignora KML em subpasta junto)
  let kmlText: string | null = null;
  let kmlNome: string | null = null;
  const arquivos = Object.keys(zip.files);
  // Prefere doc.kml na raiz (padrão Google Earth), senão pega o primeiro .kml
  const docKml = arquivos.find(fn => /^doc\.kml$/i.test(fn));
  const candidato = docKml || arquivos.find(fn => fn.toLowerCase().endsWith('.kml'));
  if (candidato) {
    try {
      kmlText = await zip.files[candidato].async('text');
      kmlNome = candidato;
    } catch (e: any) {
      throw new Error(`Falha lendo "${candidato}" do KMZ: ${e.message}`);
    }
  }
  if (!kmlText) {
    throw new Error(`KMZ não contém arquivo .kml interno (encontrados: ${arquivos.slice(0, 5).join(', ')}${arquivos.length > 5 ? '…' : ''})`);
  }
  console.log(`[geoParse] KMZ extraído "${kmlNome}" (${kmlText.length} chars)`);
  return kmlParaGeojson(kmlText);
}

/** Shapefile zipado (com .shp + .dbf + .shx + .prj) → GeoJSON */
async function shpParaGeojson(buf: ArrayBuffer): Promise<FeatureCollection> {
  const result = await shp(buf);
  // shpjs pode retornar FeatureCollection ou array delas (multi-shapefile zip)
  if (Array.isArray(result)) {
    const todasFeatures: any[] = [];
    for (const fc of result) {
      if (fc && Array.isArray(fc.features)) todasFeatures.push(...fc.features);
    }
    return { type: 'FeatureCollection', features: todasFeatures } as FeatureCollection;
  }
  return result as FeatureCollection;
}

/** Calcula métricas (área, perímetro, comprimento, bbox) do GeoJSON */
export function calcularMetricas(geojson: FeatureCollection): GeoMetricas {
  let area_m2 = 0;
  let perimetro_m = 0;
  let comprimento_m = 0;
  for (const f of geojson.features || []) {
    if (!f?.geometry) continue;
    try {
      const t = f.geometry.type;
      if (t === 'Polygon' || t === 'MultiPolygon') {
        area_m2 += turf.area(f as any);
        // Perímetro: somar comprimento das bordas
        const linha = turf.polygonToLine(f as any);
        if (linha) {
          if ((linha as any).type === 'FeatureCollection') {
            for (const sub of (linha as any).features) {
              perimetro_m += turf.length(sub, { units: 'meters' });
            }
          } else {
            perimetro_m += turf.length(linha as any, { units: 'meters' });
          }
        }
      } else if (t === 'LineString' || t === 'MultiLineString') {
        comprimento_m += turf.length(f as any, { units: 'meters' });
      }
    } catch {/* ignora geom inválida */}
  }
  // bbox via turf
  let bbox: [number, number, number, number];
  try {
    const b = turf.bbox(geojson);
    bbox = [b[0], b[1], b[2], b[3]];
  } catch {
    bbox = [-180, -90, 180, 90];
  }
  return {
    area_m2: Math.round(area_m2 * 100) / 100,
    perimetro_m: Math.round(perimetro_m * 100) / 100,
    comprimento_m: Math.round(comprimento_m * 100) / 100,
    bbox,
    n_features: (geojson.features || []).length,
  };
}

/** Lê arquivo, detecta tipo e converte para GeoJSON + métricas */
export async function parseGeoArquivo(file: File): Promise<GeoParseResult> {
  const tipo = detectarTipoGeo(file.name);
  if (!tipo) throw new Error('Formato não suportado. Aceito: .kml, .kmz, .zip (shapefile), .geojson');

  let geojson: FeatureCollection;
  if (tipo === 'kml') {
    const txt = await arquivoComoTexto(file);
    geojson = kmlParaGeojson(txt);
  } else if (tipo === 'kmz') {
    const buf = await arquivoComoBuffer(file);
    geojson = await kmzParaGeojson(buf);
  } else if (tipo === 'shp') {
    const buf = await arquivoComoBuffer(file);
    geojson = await shpParaGeojson(buf);
  } else if (tipo === 'geojson') {
    const txt = await arquivoComoTexto(file);
    const obj = JSON.parse(txt);
    if (obj.type === 'FeatureCollection') geojson = obj;
    else if (obj.type === 'Feature') geojson = { type: 'FeatureCollection', features: [obj] };
    else throw new Error('GeoJSON inválido (esperava Feature ou FeatureCollection)');
  } else {
    throw new Error('Tipo desconhecido');
  }

  if (!geojson.features?.length) {
    throw new Error('Arquivo não contém features geográficas');
  }

  const metricas = calcularMetricas(geojson);
  return { geojson, metricas, tipo };
}

/** Formata área para exibição (ha quando ≥10000m², senão m²) */
export function fmtArea(m2: number | null | undefined): string {
  if (!m2 || m2 < 1) return '—';
  if (m2 >= 10000) return `${(m2 / 10000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha`;
  return `${m2.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} m²`;
}

/** Formata distância para exibição (km quando ≥1000m, senão m) */
export function fmtDistancia(m: number | null | undefined): string {
  if (!m || m < 0.1) return '—';
  if (m >= 1000) return `${(m / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} km`;
  return `${m.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} m`;
}
